// Settings "Go module paths" (ADR-0038 sections 2 and 12, api.md 7.1): the module path prefixes a tenant publishes
// under. Add (rules checked as you type), delete (409 while a live module uses it), restore, purge and impact.
import { useState, type FormEvent } from "react";
import type { Domain, GoPrefix, Pending, TenantDetail } from "../api/types";
import { formatTime } from "../lib/format";
import { goNoSumDb, goPrefixProblem, onVerifiedDomain } from "../lib/go";
import { useServices } from "./context";
import { PendingFilter, useGrace, type PendingRow } from "./Grace";
import { IdCell } from "./IdCell";
import { CodeBlock, ErrorBanner, Section, useAction } from "./ui";
import { useLoad } from "./useLoad";

export const GO_PREFIX_HELP =
  'Go modules can be published only under these paths, for example "go.acme.com" or "github.com/acme". The first element needs a dot. On github.com, gitlab.com and other shared code hosts, write at least two elements.';

/** The live prefixes of a tenant, for the Connect page and the Settings list. Readers can list them. */
export function useGoPrefixes(tenant: TenantDetail) {
  const { api } = useServices();
  return useLoad(() => api.listGoPrefixes(tenant.slug), [tenant.slug]);
}

export function GoPrefixes({ tenant, domains }: { tenant: TenantDetail; domains: Domain[] }) {
  const { api } = useServices();
  const list = useGoPrefixes(tenant);
  const action = useAction();
  const [prefix, setPrefix] = useState("");
  const grace = useGrace<GoPrefix>({
    tenant,
    onChange: list.reload,
    ops: {
      key: "go-prefixes",
      listPending: () => api.listGoPrefixes(tenant.slug, true) as Promise<{ items: Pending<GoPrefix>[] }>,
      remove: (t) => api.deleteGoPrefix(tenant.slug, t.id),
      restore: async (id) => void (await api.restoreGoPrefix(tenant.slug, id)),
      purge: (id, confirm) => api.purgeGoPrefix(tenant.slug, id, confirm),
    },
  });
  const items = list.data?.items ?? [];
  const value = prefix.trim();
  const problem = value ? goPrefixProblem(value) : null;

  const pendingRows: PendingRow[] = grace.items.map((p) => ({
    id: p.id,
    title: <span className="mono">{p.prefix}</span>,
    deletedBy: p.deleted_by,
    deletedAt: p.deleted_at,
    purgeAfter: p.purge_after,
    purge: {
      id: p.id,
      title: `Go module path ${p.prefix}`,
      cascade: "Modules under it stay as they are. Add the path again to publish under it.",
      confirm: p.prefix,
      confirmLabel: "prefix",
    },
  }));

  async function onAdd(e: FormEvent) {
    e.preventDefault();
    if (!value || problem) return;
    if (await action.run(async () => void (await api.addGoPrefix(tenant.slug, value)))) {
      setPrefix("");
      list.reload();
      grace.reloadPending();
    }
  }

  return (
    <>
      <Section
        title="Go module paths"
        actions={<PendingFilter view={grace.view} setView={grace.setView} count={grace.count} />}
      >
        <p className="muted">{GO_PREFIX_HELP}</p>
        <ErrorBanner error={list.error ?? grace.error} />
        {grace.view === "pending" ? (
          grace.rowsTable(pendingRows)
        ) : (
          <>
            <table className="stack data-table" aria-label="Go module paths">
              <thead>
                <tr>
                  <th>Prefix</th>
                  <th>Modules</th>
                  <th>Added</th>
                  <th>ID</th>
                  <th className="actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((p) => (
                  <tr key={p.id}>
                    <td className="mono">
                      {p.prefix}{" "}
                      {onVerifiedDomain(p.prefix, domains) && (
                        <span
                          className="tag ok"
                          title="The host is a verified domain of this tenant. This is only a hint: Go does not check it."
                        >
                          verified domain
                        </span>
                      )}
                    </td>
                    <td data-label="Modules">{p.modules}</td>
                    <td data-label="Added">{formatTime(p.created_at)}</td>
                    <IdCell id={p.id} />
                    <td className="actions" data-label="Actions">
                      {grace.isAdmin && (
                        <button
                          type="button"
                          className="danger"
                          disabled={grace.busy}
                          onClick={() =>
                            grace.askDelete({
                              id: p.id,
                              title: `Go module path ${p.prefix}`,
                              message:
                                "New publishes under it stop. You can delete it only when no live Go module uses it.",
                              impact: "go/prefixes",
                            })
                          }
                        >
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {list.data && items.length === 0 && <p className="muted">No Go module paths yet.</p>}
            {items.length > 0 && (
              <CodeBlock
                title="GONOSUMDB for this tenant"
                code={`go env -w GONOSUMDB='${goNoSumDb(items.map((p) => p.prefix))}'\n`}
              />
            )}
          </>
        )}
      </Section>
      {grace.ui}
      {grace.isAdmin && (
        <Section title="Add a Go module path">
          <form onSubmit={onAdd} className="form">
            <label>
              Prefix
              <input
                required
                value={prefix}
                onChange={(e) => setPrefix(e.target.value)}
                placeholder="go.acme.com"
                autoComplete="off"
                spellCheck={false}
                aria-invalid={problem ? true : undefined}
                aria-describedby="go-prefix-problem"
              />
              <small id="go-prefix-problem" className="muted">
                Lowercase. At most 20 paths for each tenant.
              </small>
            </label>
            {problem && (
              <p className="banner error" role="alert">
                {problem}
              </p>
            )}
            <ErrorBanner error={action.error} />
            <button type="submit" disabled={action.busy || !value || Boolean(problem)}>
              Add Go module path
            </button>
          </form>
        </Section>
      )}
    </>
  );
}
